/* eslint-disable jsx-a11y/label-has-for */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import classNames from 'classnames';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { v4 as uuid } from 'uuid';

import subscriberApi from 'utils/subscriberApi';
import newsletterApi from 'utils/newsletterApi';
import NewsletterList from './components/newsletter-list';
import FormError from './components/form-error';
import './styles.scss';

const {
  i18n: {
    __,
    sprintf,
  },
} = wp;

const {
  url: {
    getQueryArg,
  } = {},
} = wp;

const emailInUrl = getQueryArg(window.location.href, 'e') || '';

export default class Newsletters extends PureComponent {
  /**
   * Set PropTypes for this component.
   *
   * @type {Object}
   */
  static propTypes = {
    nonce: PropTypes.string.isRequired,
    successMessage: PropTypes.string,
  };

  /**
   * Set default props.
   *
   * @type {Object}
   */
  static defaultProps = {
    successMessage: '',
  };

  /**
   * Does a list have sub newsletters.
   *
   * @param {Object} newsletter The newsletter.
   * @returns {boolean}
   */
  static hasSubNewsletters(newsletter) {
    return 0 < newsletter.sub_newsletters.length;
  }

  /**
   * Utility for maybe checking a sub newsletter.
   *
   * @param {Array} subscribedLists
   * @returns {Function}
   */
  static maybeCheckSubNewsletter(subscribedLists) {
    return (newsletter) => {
      if (Newsletters.hasSubNewsletters(newsletter)) {
        const subNewsletters =
          newsletter.sub_newsletters.map((subNewsletter) => {
            if (subscribedLists
              .includes(subNewsletter.sub_newsletter_id)) {
              return {
                ...subNewsletter,
                isChecked: true,
              };
            }
            return subNewsletter;
          });

        return {
          ...newsletter,
          sub_newsletters: subNewsletters,
        };
      }

      // Otherwise, just return the newsletter object.
      return newsletter;
    };
  }

  /**
   * Maybe check a parent.
   *
   * If all sub-newsletters are checked, then check the parent.
   *
   * @param {object} newsletter
   * @returns {Object}
   */
  static maybeCheckParent(newsletter) {
    if (! Newsletters.hasSubNewsletters(newsletter)) {
      return newsletter;
    }

    const checkedNewsletters =
      newsletter
        .sub_newsletters
        .filter((subNewsletter) => subNewsletter.isChecked);

    // Note: This is terse and can probably be simplified. But it works.
    let isChecked;
    if (0 === checkedNewsletters.length) {
      isChecked = false;
    } else {
      isChecked = true;
    }

    return {
      ...newsletter,
      isChecked,
    };
  }

  /**
   * Maybe check a newsletter.
   *
   * For use when mapping over newsletters.
   *
   * @param {Array} subscribedLists
   * @returns {Function}
   */
  static maybeCheckNewsletter(subscribedLists) {
    return (newsletter) => {
      if (subscribedLists.includes(newsletter.newsletter_id)) {
        return {
          ...newsletter,
          isChecked: true,
        };
      }

      return newsletter;
    };
  }

  /**
   * Transform the initial newsletters data.
   *
   * Specifically, add in an isChecked value. This data is the source for checkboxes.
   *
   * @param {Array} newsletters
   * @returns {Array}
   */
  static transformInitialNewsletterData(newsletters) {
    return newsletters.map((newsletter) => {
      const currentNewsletter = newsletter;

      if (! currentNewsletter.newsletter_id) {
        currentNewsletter.newsletter_id = uuid();
      }

      // Process sub newsletters.
      const subNewsletters =
        currentNewsletter.sub_newsletters.map((subNewsletter) => ({
          ...subNewsletter,
          isChecked: false,
        }));

      // Return the transformed data.
      return {
        ...currentNewsletter,
        sub_newsletters: subNewsletters,
        isChecked: false,
      };
    });
  }

  state = {
    currentStep: 1,
    formData: {
      email: emailInUrl,
      fname: '',
      lname: '',
      zip: '',
      lists: {},
    },
    newsletters: [],
    isLoading: false,
    errors: {},
    showSuccess: false,
    buttonDisabled: true,
    formReady: false,
    captchaSolved: false,
  };

  /**
   * Hydrate lists.
   */
  componentDidMount() {
    const fetch = newsletterApi.fetchNewsletters();

    fetch
      .then((newsletters) => {
        // Transform the newsletters data.
        const newNewsletters =
          Newsletters.transformInitialNewsletterData(newsletters);

        this.setState({
          newsletters: newNewsletters,
          isLoading: false,
        });
      });
  }

  /**
   * Handle a checkbox change.
   *
   */
  setCaptchaSolved = () => {
    this.setState({ captchaSolved: true });
    if (this.state.formReady) {
      this.setState({ buttonDisabled: false });
    }
  };

  /**
   * Handle a checkbox change.
   *
   * @param {Boolean} checkAll If true, check all sub newsletters.
   * @param {String} name The name of the list.
   */
  handleCheck = (checkAll, name) => {
    if (this.state.captchaSolved) {
      this.setState({
        buttonDisabled: false,
        formReady: true,
      });
    } else {
      this.setState({ formReady: true });
    }

    this.setState(
      (prevState) => ({ newsletters: prevState.newsletters.map((newsletter) => {
        const value = ! newsletter.isChecked;
        if (checkAll && name === newsletter.newsletter_id) {
          const parent = {
            ...newsletter,
            isChecked: value,
          };
          const subNewsletters =
            newsletter.sub_newsletters.map((subNewsletter) => ({
              ...subNewsletter,
              isChecked: value,
            }));
          return {
            ...parent,
            sub_newsletters: [...subNewsletters],
          };
        } else if (name === newsletter.newsletter_id) {
          return {
            ...newsletter,
            isChecked: value,
          };
        }
        return {
          ...newsletter,
        };
      }) }),
      () => {
        this.updateFormLists();
      }
    );
  };

  /**
   * Handle a sub newsletter on change.
   *
   * @param {Object} event The event object.
   * @param {Object} parentList The parent list.
   */
  handleSubNewsletterOnChange = (event, parentList) => {
    const { checked, value } = event.target;

    if (this.state.captchaSolved) {
      this.setState({
        buttonDisabled: false,
        formReady: true,
      });
    } else {
      this.setState({ formReady: true });
    }

    const updatedSubNewsletters =
      parentList.sub_newsletters.map((subNewsletter) => {
        if (value === subNewsletter.sub_newsletter_id) {
          return {
            ...subNewsletter,
            isChecked: checked,
          };
        }
        return subNewsletter;
      });

    const updatedParent = {
      ...parentList,
      sub_newsletters: updatedSubNewsletters,
    };

    const allUnChecked =
      updatedSubNewsletters.filter((updated) => updated.isChecked);

    this.setState(
      (prevState) => ({ newsletters: prevState.newsletters.map((newsletter) => {
        if (updatedParent.newsletter_id === newsletter.newsletter_id) {
          return {
            ...updatedParent,
            isChecked: 0 < allUnChecked.length,
          };
        }

        return newsletter;
      }) }),
      () => {
        this.updateFormLists();
      }
    );
  };

  /**
   * Update the lists in form data.
   */
  updateFormLists = () => {
    let subNewsletters = {};
    const checkedNewsletters =
      this.state.newsletters.reduce((acc, cur) => {
        if (cur.isChecked &&
            ! Newsletters.hasSubNewsletters(cur)) {
          acc[cur.newsletter_id] = 1;
        }

        if (! cur.isChecked && ! Newsletters.hasSubNewsletters(cur)) {
          acc[cur.newsletter_id] = 0;
        }

        // Handle sub newsletters.
        if (0 < cur.sub_newsletters.length) {
          subNewsletters =
            cur.sub_newsletters.reduce((subAcc, subCur) => {
              if (subCur.isChecked) {
                subAcc[subCur.sub_newsletter_id] = 1; // eslint-disable-line no-param-reassign
              } else {
                subAcc[subCur.sub_newsletter_id] = 0; // eslint-disable-line no-param-reassign
              }

              return subAcc;
            }, {});
        }

        return { ...acc, ...subNewsletters };
      }, {});

    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        lists: checkedNewsletters,
      },
    }));
  };

  /**
   * Handle a string change.
   *
   * @param {Object} e The event.
   */
  handleStringChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value.trim(),
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));
  };

  /**
   * Handle a zip code change.
   *
   * @param {Object} e The event.
   */
  handleZipChange = (e) => {
    const { name, value } = e.target;
    const numbersOnly = (value.match(/\d/g) || []).join('').substring(0, 5);
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: numbersOnly,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));
  };

  /**
   * Handle step one of form submit.
   *
   * @param {Object} event The event object.
   */
  handleStepOneSubmit = (event) => {
    event.preventDefault();
    let errors = {};
    this.setState(
      (prevState) => {
        errors = this.validateStepOne(prevState.formData);
        return { errors };
      }
    );
    this.setState({ errors });

    if (0 === Object.keys(errors).length) {
      this.setState({ isLoading: true });
      const fetch = subscriberApi.fetchSubscriber(this.state.formData.email);

      // Check to see if subscriber exists.
      fetch
        .then((user) => {
          // Success! We found a user. Let them update their subscriptions.
          const { newsletters } = this.state;
          const {
            lists,
            fname,
            lname,
            zip,
          } = user[0];

          // Get an array of the lists they are subscribed to.
          const subscribedLists = Object.keys(lists);
          let newNewsletters = [];

          if ('newsletter_id' !== subscribedLists[0]) {
            newNewsletters =
              newsletters
                .map(Newsletters.maybeCheckSubNewsletter(subscribedLists))
                .map(Newsletters.maybeCheckParent)
                .map(Newsletters.maybeCheckNewsletter(subscribedLists));
          } else {
            newNewsletters = [...newsletters];
          }

          return this.setState((prevState) => ({
            ...prevState,
            formData: {
              ...prevState.formData,
              fname,
              lname,
              zip,
            },
            newsletters: newNewsletters,
            isLoading: false,
          }), () => {
            this.updateFormLists();
            this.setState({
              currentStep: 3,
              isLoading: false,
            });
          });
        }).catch((response) => {
          if ('sailthru-invalid-email' === response.code) {
            // Failure! The email entered is invalid, according to sailthru.
            this.setState({
              isLoading: false,
              errors: {
                email: __('Invalid email address', 'nbc'),
              },
            });
          } else if ('sailthru-user-not-found' === response.code) {
            // Success! Valid email, but not registered yet. Let them sign up.
            this.setState({
              currentStep: 2,
              isLoading: false,
            });
          } else {
            // Failure!
            this.setState({
              isLoading: false,
              errors: {
                email: __('Something went wrong. Please try again.', 'nbc'),
              },
            });
          }
        });
    }
  };

  /**
   * Handle step two of form submit.
   *
   * @param {Object} event The event object.
   */
  handleStepTwoSubmit = (event) => {
    event.preventDefault();
    let errors = {};
    this.setState(
      (prevState) => {
        errors = this.validateStepTwo(prevState.formData);
        return { errors };
      }
    );

    if (0 === Object.keys(errors).length) {
      this.setState({ isLoading: true });
      const { formData } = this.state;
      const { nonce } = this.props;

      const fetch = subscriberApi.updateSubscriber(formData, nonce);

      fetch
        .then((response) => {
          if (response.ok) {
            this.updateFormLists();
            this.setState({
              currentStep: 3,
              isLoading: false,
            });
          }
        })
        .catch((response) => {
          if ('sailthru-invalid-email' === response.code) {
            // Failure! The email entered is invalid, according to sailthru.
            this.setState((prevState) => {
              const state = {
                ...prevState,
                isLoading: false,
                errors: {
                  email: __('Invalid email address', 'nbc'),
                },
              };

              if (window.matchMedia('screen and (max-width: 767px)').matches) {
                state.errors.signup =
                  __('Please check the form for errors.', 'nbc');
              }

              return state;
            });
          } else {
            this.setState((prevState) => ({
              ...prevState,
              isLoading: false,
              errors: {
                signup: __('An error occurred. Please try again.', 'nbc'),
              },
            }));
          }
        });
    }
  };

  validateStepOne = (formData) => {
    const errors = {};

    if (! formData.email) errors.email = __('Cannot be blank', 'nbc');

    return errors;
  };

  validateStepTwo = (formData) => {
    const errors = {};

    if (! formData.email) errors.email = __('Cannot be blank', 'nbc');
    if (! formData.fname) errors.fname = __('Cannot be blank', 'nbc');
    if (! formData.lname) errors.lname = __('Cannot be blank', 'nbc');
    if (! formData.zip) errors.zip = __('Cannot be blank', 'nbc');

    return errors;
  };

  handleStepThreeSubmit = (event) => {
    event.preventDefault();
    this.setState({ isLoading: true });
    const { formData } = this.state;
    const { nonce } = this.props;

    const fetch = subscriberApi.updateSubscriber(formData, nonce);

    fetch
      .then((response) => {
        if (response.ok) {
          this.updateFormLists();
          this.setState({
            isLoading: false,
            showSuccess: true,
            buttonDisabled: true,
          });
        }
      });
  };

  render() {
    const {
      currentStep,
      formData: {
        email,
        fname,
        lname,
        zip,
      },
      newsletters,
      isLoading,
      errors,
      showSuccess,
      buttonDisabled,
    } = this.state;

    const {
      successMessage,
      isTelemundo,
      siteKey,
    } = this.props;

    const newslettersCSS = classNames({
      newsletters: true,
      loading: isLoading,
      step1: 1 === currentStep,
      step2: 2 === currentStep,
      step3: 3 === currentStep,
    });

    return (
      <div className={newslettersCSS}>
        {
          1 === currentStep && (
            <form
              className="newsletters__form"
              onSubmit={this.handleStepOneSubmit}
            >
              <div className="newsletters__form-row">
                <div
                  className="newsletters__form-field three-quarters"
                >
                  <label
                    htmlFor="email"
                    className="required"
                  >
                    {__('Enter your email', 'nbc')}
                  </label>
                  <input
                    name="email"
                    type="email"
                    id="email"
                    value={email}
                    onChange={this.handleStringChange}
                    required
                    placeholder={__('ex: mailAddress@client.com', 'nbc')}
                  />
                  {
                    errors.email &&
                    <FormError errorMessage={errors.email} />
                  }
                </div>
                <div className="newsletters__form-field">
                  <button className="newsletters__button" type="submit">
                    <span className="newsletters__button-text">
                      {
                        isLoading ?
                          __('Submitting...', 'nbc') :
                          __('Sign in / Sign up', 'nbc')
                      }
                    </span>
                  </button>
                </div>
              </div>
              <hr className="newsletters__hr" />
              <div className="newsletters__form-row">
                <NewsletterList
                  newsletters={newsletters}
                  handleCheck={this.handleCheck}
                  handleSubNewsletterOnChange={
                    this.handleSubNewsletterOnChange
                  }
                  disableNewsletters
                />
              </div>
            </form>
          )
        }
        {
          2 === currentStep && (
            <form
              className="newsletters__form"
              onSubmit={this.handleStepTwoSubmit}
            >
              <div className="newsletters__form-row">
                <div
                  className="newsletters__form-field three-quarters"
                >
                  <label
                    htmlFor="email"
                    className="required"
                  >
                    {__('Email', 'nbc')}
                  </label>
                  <input
                    name="email"
                    type="email"
                    id="email"
                    value={email}
                    onChange={this.handleStringChange}
                    placeholder={__('ex: mailAddress@client.com', 'nbc')}
                    required
                  />
                  {
                    errors.email &&
                    <FormError errorMessage={errors.email} />
                  }
                </div>
                <div className="newsletters__form-field">
                  <input
                    type="submit"
                    value={__('Sign up / Sign in', 'nbc')}
                    disabled
                  />
                </div>
              </div>
              <hr className="newsletters__hr" />
              <div className="newsletters__form-row two-column">
                <div className="newsletters__form-field two-column">
                  <label
                    htmlFor="fname"
                    className="required"
                  >
                    {__('First name', 'nbc')}
                  </label>
                  <input
                    name="fname"
                    type="text"
                    id="fname"
                    value={fname}
                    onChange={this.handleStringChange}
                    required
                  />
                  {
                    errors.fname &&
                    <FormError errorMessage={errors.fname} />
                  }
                </div>
                <div className="newsletters__form-field two-column">
                  <label
                    htmlFor="lname"
                    className="required"
                  >
                    {__('Last name', 'nbc')}
                  </label>
                  <input
                    name="lname"
                    type="text"
                    id="lname"
                    value={lname}
                    onChange={this.handleStringChange}
                    required
                  />
                  {
                    errors.lname &&
                    <FormError errorMessage={errors.lname} />
                  }
                </div>
              </div>
              <div className="newsletters__form-row">
                <div className="newsletters__form-field">
                  <label
                    htmlFor="zip"
                    className="required"
                  >
                    {__('Zip code', 'nbc')}
                  </label>
                  <input
                    name="zip"
                    type="text"
                    id="zip"
                    value={zip}
                    onChange={this.handleZipChange}
                    required
                  />
                  {
                    errors.zip &&
                    <FormError errorMessage={errors.zip} />
                  }
                </div>
              </div>
              <hr className="newsletters__hr" />
              <div
                className="newsletters__form-row \
                newsletters__form-row--finish"
              >
                <div className="newsletters__form-field">
                  <button className="newsletters__button" type="submit">
                    <span className="newsletters__button-text">
                      {
                        isLoading ?
                          __('Submitting...', 'nbc') :
                          __('Finish sign up', 'nbc')
                      }
                    </span>
                  </button>
                </div>
                {
                  errors.signup &&
                  <span className="error-msg newsletters__signup-error">
                    {errors.signup}
                  </span>
                }
              </div>
              <div className="newsletters__form-row">
                <NewsletterList
                  newsletters={newsletters}
                  handleCheck={this.handleCheck}
                  handleSubNewsletterOnChange={
                    this.handleSubNewsletterOnChange
                  }
                  disableNewsletters
                />
              </div>
            </form>
          )
        }
        {
          3 === currentStep && (
            <Fragment>
              <div className="newsletters__logged-in">
                <h3 className="newsletters__welcome">
                  {
                    sprintf(
                      /* translators: 1: welcome messagel */
                      /* eslint-disable max-len */
                      __('Choose the newsletters you\'d like to receive.', 'nbc'),
                      /* eslint-enable max-len */
                      fname,
                      lname,
                    )
                  }
                </h3>
                <a
                  className="newsletters__button button-secondary"
                  href={window.location.href}
                >
                  {__('Log out', 'nbc')}
                </a>
              </div>
              <hr className="newsletters__hr" />
              {
                showSuccess && (
                  <div className="newsletters__flash">
                    <button
                      className="newsletters__flash-close"
                      onClick={() => this.setState({ showSuccess: false })}
                      type="button"
                    >
                      <span className="screen-reader-text">
                        {
                          __('Close success message', 'nbc')
                        }
                      </span>
                    </button>
                    <div className="newsletters__flash-message">
                      {parse(DOMPurify.sanitize(JSON.parse(successMessage)))}
                    </div>
                  </div>
                )
              }
              <form
                className="newsletters_form"
                onSubmit={this.handleStepThreeSubmit}
              >
                <div className="newsletters__form-row">
                  <NewsletterList
                    newsletters={newsletters}
                    handleCheck={this.handleCheck}
                    handleSubNewsletterOnChange={
                      this.handleSubNewsletterOnChange
                    }
                  />
                </div>
                <ReCAPTCHA
                  sitekey={siteKey}
                  onChange={this.setCaptchaSolved}
                  className="recaptcha-container"
                  hl={isTelemundo ? 'es' : 'en'}
                />
                <div className="newsletters__form-row">
                  <div className="newsletters__form-field">
                    <button
                      className="newsletters__button"
                      type="submit"
                      disabled={buttonDisabled}
                    >
                      <span className="newsletters__button-text">
                        {
                          isLoading ?
                            __('Submitting...', 'nbc') :
                            __('Save Changes', 'nbc')
                        }
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            </Fragment>
          )
        }
      </div>
    );
  }
}
